import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Wraps a page/route. If the current user is missing the required permission,
 * shows a toast and redirects to /dashboard. Used to harden direct-URL access
 * for routes that aren't safe to expose to every authenticated tenant user.
 *
 * Usage:
 *   <RequirePermission perm="coach.write">
 *     <Page />
 *   </RequirePermission>
 *
 * Or with multiple:
 *   <RequirePermission anyOf={["orders.write", "orders.admin"]}>
 */
export default function RequirePermission({ perm, anyOf, children, fallback = "/dashboard" }) {
  const { user } = useAuth();
  const location = useLocation();

  const perms = new Set(user?.permissions || []);
  const required = perm ? [perm] : (anyOf || []);
  const allowed = required.length === 0
    || required.some((p) => perms.has(p))
    || user?.role === "SUPER_ADMIN";

  useEffect(() => {
    if (!allowed) {
      toast.error("Accès refusé", {
        description: `Votre rôle ${user?.role || "actuel"} n'a pas la permission requise.`,
      });
    }
  }, [allowed, user?.role]);

  if (!user) return null;
  if (!allowed) {
    return <Navigate to={fallback} replace state={{ from: location }} />;
  }
  return children;
}

/** Renders children only if the user has the permission, no redirect. */
export function HasPermission({ perm, anyOf, children, fallback = null }) {
  const { user } = useAuth();
  const perms = new Set(user?.permissions || []);
  const required = perm ? [perm] : (anyOf || []);
  const allowed = required.length === 0
    || required.some((p) => perms.has(p))
    || user?.role === "SUPER_ADMIN";
  return allowed ? children : fallback;
}
